import { useEffect, useRef } from 'react';

const DISMISS_THRESHOLD = 0.35;

type Options = {
  enabled: boolean;
  onClose: () => void;
  modalId: string;
};

const useSwipeToDismiss = ({ enabled, onClose, modalId }: Options) => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let teardown: (() => void) | undefined;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;

      const paper = document.querySelector(`[data-openx-modal-paper="${modalId}"]`) as HTMLElement | null;
      if (!paper) return;

      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      const isWithinHeader = (target: Element | null) => !!target?.closest('[class*="Dialogstyles__Header"]');

      const onTouchStart = (e: TouchEvent) => {
        const target = e.target as Element | null;
        if (!isWithinHeader(target)) return;
        if (target?.closest('[data-swipe-dismiss-ignore]')) return;
        startY = e.touches[0].clientY;
        currentY = startY;
        isDragging = true;
        paper.style.transition = 'none';
      };

      const onTouchMove = (e: TouchEvent) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const deltaY = currentY - startY;
        if (deltaY < 0) {
          paper.style.transform = '';
          return;
        }
        e.preventDefault();
        paper.style.transform = `translateY(${deltaY}px)`;
      };

      const onTouchEnd = () => {
        if (!isDragging) return;
        isDragging = false;
        const shouldDismiss = (currentY - startY) / paper.offsetHeight >= DISMISS_THRESHOLD;

        paper.style.transition = 'transform 200ms ease-out';
        paper.style.transform = shouldDismiss ? 'translateY(100%)' : '';

        setTimeout(() => {
          if (shouldDismiss) {
            paper.style.animation = 'none';
            onCloseRef.current();
          } else {
            paper.style.transition = '';
          }
        }, 200);
      };

      paper.addEventListener('touchstart', onTouchStart, { passive: true });
      paper.addEventListener('touchmove', onTouchMove, { passive: false });
      paper.addEventListener('touchend', onTouchEnd, { passive: true });

      teardown = () => {
        paper.removeEventListener('touchstart', onTouchStart);
        paper.removeEventListener('touchmove', onTouchMove);
        paper.removeEventListener('touchend', onTouchEnd);
        paper.style.transform = '';
        paper.style.transition = '';
      };
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      teardown?.();
    };
  }, [enabled, modalId]);
};

export default useSwipeToDismiss;
